import { useState, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addPantoneColor, updatePantoneColor } from 'helpers/backend_helper';
import { Common } from 'helpers/common';

export const usePantoneColorModal = (onSave: (result: any) => void, initialData?: any) => {
    const [show, setShow] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [pantoneColorData, setPantoneColorData] = useState<any>(initialData);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const commonHelper = new Common();

    useEffect(() => {
        if (initialData) {
            setPantoneColorData(initialData);
            setIsEdit(!!initialData.id);
        }
    }, [initialData]);

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setPantoneColorData(null);
        }
    }, [show]);

    const handleEditDataClick = (ele: any) => {
        setPantoneColorData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddPantoneColor = () => {
        setPantoneColorData({});
        toggle();
    };

    const validation: any = useFormik({
    enableReinitialize: true,
        initialValues: {
            code: (pantoneColorData && pantoneColorData.code) || "",
            name: (pantoneColorData && pantoneColorData.name) || "",
            hex_colour: (pantoneColorData && pantoneColorData.hex_colour) || "",
            l_value: (pantoneColorData && pantoneColorData.l_value) || "",
            a_value: (pantoneColorData && pantoneColorData.a_value) || "",
            b_value: (pantoneColorData && pantoneColorData.b_value) || "",
            rgb_r: (pantoneColorData && pantoneColorData.rgb_r) || "",
            rgb_g: (pantoneColorData && pantoneColorData.rgb_g) || "",
            rgb_b: (pantoneColorData && pantoneColorData.rgb_b) || "",
        },
    validationSchema: Yup.object({
      code: Yup.string().required("required"),
      name: Yup.string().required("required"),
      hex_colour: Yup.string()
        .matches(/^#[0-9A-F]{6}$/i, "invalid format")
        .required("required"),
      l_value: Yup.number()
        .min(0, "must be positive")
        .max(100, "must be less than 100")
        .required("required"),
      a_value: Yup.number()
        .min(-128, "invalid")
        .max(127, "invalid")
        .required("required"),
      b_value: Yup.number()
        .min(-128, "invalid")
        .max(127, "invalid")
        .required("required"),
      rgb_r: Yup.number()
        .min(0, "invalid")
        .max(255, "invalid")
        .required("required"),
      rgb_g: Yup.number()
        .min(0, "invalid")
        .max(255, "invalid")
        .required("required"),
      rgb_b: Yup.number()
        .min(0, "invalid")
        .max(255, "invalid")
        .required("required"),
    }),

        onSubmit: async (values) => {
            try {
                setIsSaving(true);
                let result;
                const formattedValues = {
                    ...values,
                    l_value: parseFloat(values.l_value),
                    a_value: parseFloat(values.a_value),
                    b_value: parseFloat(values.b_value),
                    rgb_r: parseInt(values.rgb_r),
                    rgb_g: parseInt(values.rgb_g),
                    rgb_b: parseInt(values.rgb_b),
                };

                if (isEdit && pantoneColorData?.id) {
                    result = await updatePantoneColor({
                        id: pantoneColorData.id,
                        ...formattedValues,
                    });
                    commonHelper.showSuccessMessage('Pantone Color updated successfully!');
                } else {
                    result = await addPantoneColor(formattedValues);
                    commonHelper.showSuccessMessage('Pantone Color added successfully!');
                }
                
                onSave(result);
                validation.resetForm();
            } catch (error: any) {
                commonHelper.showErrorMessage('Operation failed!');
            } finally {
                setIsSaving(false);
            }
        },
    });

    return {
        show,
        isEdit,
        pantoneColorData,
        toggle,
        handleEditDataClick,
        handleAddPantoneColor,
        validation,
        isSaving,
    };
};

